import React, { useMemo, useState } from 'react';
import { Chart } from '../../../../../dashboard/components/Chart/Chart';
import { ToggleGroup } from '@plerionhq/design-system/experimental';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

export type TrendMetric = 'totalTokens' | 'messageCount';

const trendOptions = (
  t: TFunction<'pleri/settings'>,
): { value: TrendMetric; label: string }[] => [
  { value: 'totalTokens', label: t('tokens') },
  { value: 'messageCount', label: t('questionsAsked') },
];

export type TrendDataPoint =
  | {
      label: string;
      totalTokens: number;
      messageCount: number;
      conversationCount: number;
    }
  | Record<
      string,
      | string
      | number
      | { totalTokens: number; messageCount: number; conversationCount: number }
    >;

function hasKey(o: unknown, key: string): o is Record<string, unknown> {
  return typeof o === 'object' && o !== null && key in o;
}

function isMetricBucket(v: unknown): v is {
  totalTokens: number;
  messageCount: number;
  conversationCount: number;
} {
  return (
    typeof v === 'object' &&
    v !== null &&
    'totalTokens' in v &&
    'messageCount' in v &&
    'conversationCount' in v &&
    typeof (v as Record<string, unknown>).totalTokens === 'number' &&
    typeof (v as Record<string, unknown>).messageCount === 'number' &&
    typeof (v as Record<string, unknown>).conversationCount === 'number'
  );
}

function getMetricFromGroupedPoint(
  rec: Record<string, unknown>,
  key: string,
  metric: TrendMetric,
): number {
  const v = rec[key];
  if (isMetricBucket(v)) {
    return metric === 'totalTokens' ? v.totalTokens : v.messageCount;
  }
  return 0;
}

export function TrendSection({
  data,
  seriesKeys,
  seriesLabels,
}: {
  data: TrendDataPoint[];
  seriesKeys: string[] | null;
  seriesLabels?: string[] | null;
}) {
  const { t } = useTranslation('pleri/settings');
  const [metric, setMetric] = useState<TrendMetric>('totalTokens');

  const isGrouped = seriesKeys != null && seriesKeys.length > 0;
  const chartData = useMemo(() => {
    if (!isGrouped) {
      return data.filter(
        (
          d,
        ): d is {
          label: string;
          totalTokens: number;
          messageCount: number;
          conversationCount: number;
        } =>
          typeof d === 'object' &&
          d !== null &&
          'label' in d &&
          'totalTokens' in d &&
          'messageCount' in d &&
          'conversationCount' in d,
      );
    }
    return data.map((point) => {
      const out: Record<string, string | number> = {};
      if (
        typeof point === 'object' &&
        point !== null &&
        'label' in point &&
        typeof point.label === 'string'
      ) {
        out.label = point.label;
      }
      for (const key of seriesKeys) {
        out[key] = hasKey(point, key)
          ? getMetricFromGroupedPoint(point, key, metric)
          : 0;
      }
      return out;
    });
  }, [data, isGrouped, seriesKeys, metric]);

  const chartFields = isGrouped
    ? { category: 'label' as const, values: seriesKeys }
    : { category: 'label' as const, values: metric };
  const fieldNames = useMemo(() => {
    const base: Record<string, string> = {
      totalTokens: 'Tokens',
      messageCount: 'Questions asked',
    };
    if (isGrouped && seriesKeys) {
      seriesKeys.forEach((k, i) => {
        base[k] =
          seriesLabels != null && seriesLabels[i] != null
            ? seriesLabels[i]
            : k.replace(/_/g, ' ');
      });
    }
    return base;
  }, [isGrouped, seriesKeys, seriesLabels]);

  const trendMetricItems = trendOptions(t).map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  if (data.length === 0) {
    return (
      <p className="py-10 text-center text-sm leading-relaxed text-gray-500 dark:text-gray-400">
        {t('noDataForThisPeriod')}
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4">
        <ToggleGroup
          type="single"
          value={metric}
          onValueChange={(v) => {
            if (v === 'totalTokens' || v === 'messageCount') {
              setMetric(v);
            }
          }}
          items={trendMetricItems}
        />
      </div>
      <div className="mt-3">
        <Chart
          type="bar"
          data={chartData}
          fields={chartFields}
          fieldNames={fieldNames}
          height={320}
          showLegend={isGrouped}
          layout={isGrouped ? 'stacked' : undefined}
          colors={
            isGrouped
              ? [
                  '#0d9488',
                  '#4f46e5',
                  '#7c3aed',
                  '#059669',
                  '#0284c7',
                  '#db2777',
                  '#d97706',
                  '#0891b2',
                ]
              : ['#f97316']
          }
        />
      </div>
    </div>
  );
}
